import { useState, useCallback } from 'react';

const distanceCache = new Map();
const CACHE_EXPIRY = 30 * 60 * 1000;

const createCacheEntry = (data) => ({
  data,
  timestamp: Date.now(),
  expiry: Date.now() + CACHE_EXPIRY
});

const isCacheValid = (entry) => {
  return entry && Date.now() < entry.expiry;
};

const getCacheKey = (from, to) => {
  return `${from.toLowerCase().trim()}|${to.toLowerCase().trim()}`;
};

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const estimateDurationMinutes = (distanceKm) => {
  const roadFactor = 1.3;
  const avgSpeedKmh = 30;
  const estimatedRoadKm = distanceKm * roadFactor;
  return Math.round((estimatedRoadKm / avgSpeedKmh) * 60);
};

export const useDistanceCalculation = (options = {}) => {
  const {
    apiEndpoint = `/api/location/get_distance`,
    useHaversine = true,
    useGoogleAPI = false,
    cacheExpiry = CACHE_EXPIRY
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastCalculation, setLastCalculation] = useState(null);

  const calculateDistance = useCallback(async (from, to, coordinates = null) => {
    console.log('calculateDistance called with:', { from, to, coordinates });

    if (!from || !to) {
      console.log('Missing from or to address');
      setError('Both from and to addresses are required');
      return null;
    }

    const cacheKey = getCacheKey(from, to);
    const cachedEntry = distanceCache.get(cacheKey);

    if (isCacheValid(cachedEntry)) {
      console.log('Using cached distance for:', from, 'to', to);
      setLastCalculation(cachedEntry.data);
      return cachedEntry.data;
    }

    console.log('Starting distance calculation...');
    setLoading(true);
    setError(null);

    try {
      let result;

      if (useHaversine && coordinates) {
        const { fromLat, fromLng, toLat, toLng } = coordinates;
        console.log('Using haversine calculation with coordinates:', { fromLat, fromLng, toLat, toLng });

        if (fromLat && fromLng && toLat && toLng) {
          const distance = haversineDistance(fromLat, fromLng, toLat, toLng);
          const durationMin = estimateDurationMinutes(distance);
          console.log('Haversine distance calculated:', distance, 'estimated duration:', durationMin, 'min');

          result = {
            from,
            to,
            distance: {
              text: `${distance.toFixed(1)} km`,
              value: distance
            },
            duration: {
              text: `~${durationMin} min (estimated)`,
              value: durationMin
            },
            method: 'haversine'
          };
        } else {
          console.log('Invalid coordinates for haversine calculation');
        }
      }

      if (!result && useGoogleAPI) {
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ from, to })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        result = {
          from: data.from,
          to: data.to,
          distance: {
            text: data.distance,
            value: parseFloat(data.distance.replace(/[^\d.]/g, '')) || 0
          },
          duration: {
            text: data.duration,
            value: parseFloat(data.duration.replace(/[^\d.]/g, '')) || 0
          },
          method: 'google'
        };
      }

      if (!result) {
        throw new Error('No calculation method available');
      }

      distanceCache.set(cacheKey, createCacheEntry(result));

      setLastCalculation(result);
      console.log('Distance calculated:', result);

      return result;
    } catch (err) {
      console.error('Distance calculation error:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, useHaversine, useGoogleAPI]);

  const calculateDistanceWithCoords = useCallback(async (from, to, fromLat, fromLng, toLat, toLng) => {
    console.log('calculateDistanceWithCoords called with:', { from, to, fromLat, fromLng, toLat, toLng });
    return calculateDistance(from, to, { fromLat, fromLng, toLat, toLng });
  }, [calculateDistance]);

  const clearCache = useCallback(() => {
    distanceCache.clear();
    console.log('Distance cache cleared');
  }, []);

  const getCacheStats = useCallback(() => {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const [key, entry] of distanceCache.entries()) {
      if (isCacheValid(entry)) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      total: distanceCache.size,
      valid: validEntries,
      expired: expiredEntries
    };
  }, []);

  const cleanExpiredCache = useCallback(() => {
    const now = Date.now();
    for (const [key, entry] of distanceCache.entries()) {
      if (!isCacheValid(entry)) {
        distanceCache.delete(key);
      }
    }
    console.log('Expired distance cache entries cleaned');
  }, []);

  return {
    calculateDistance,
    calculateDistanceWithCoords,
    loading,
    error,
    lastCalculation,
    clearCache,
    getCacheStats,
    cleanExpiredCache,

    formatDistance: (distance) => {
      console.log('formatDistance called with:', distance);
      if (typeof distance === 'number') {
        return `${distance.toFixed(1)} km`;
      }
      return distance;
    },

    formatDuration: (duration) => {
      console.log('formatDuration called with:', duration);
      if (typeof duration === 'number') {
        return `${duration.toFixed(1)} min`;
      }
      return duration;
    }
  };
};

export default useDistanceCalculation;
